import React, { useCallback, useContext, useState } from 'react';
import { RouteProps } from 'react-router-dom';
import { PATHS } from '../components/layout/Routes';
import DataLayout from '../components/layout/DataLayout';
import { Alert, App, Button, Input, Tabs, Typography } from 'antd';
import { ThemeContext } from 'styled-components';
import Select, { SingleValue } from 'react-select';
import { SearchOutlined } from '@ant-design/icons';
import { useMsal } from '@azure/msal-react';
import gql from 'graphql-tag';
import { get, orderBy, uniqBy } from 'lodash';
import { ClientType, getClient } from '../graphql/client';
import { EntitlementActivationJSONQuery, ResetDeviceFeatures } from '../graphql/queries';
import { hasOrderAccess, hasWriteAccess } from '../helper/authms';
import AuthError from '../components/AuthError';
import Table from '../components/Table';
import Loader from '../components/Loader';
import { formatFastspringLink, formatTimestamp } from '../formatter/pool';

const { Text } = Typography;

type VersionedFeature = { key: string; version: string; data: any };
type DeviceInfo = {
  productCode: string;
  modelId: string;
  manufacturerId: string;
  licenseId: string;
  serialNumber: string;
  device: object;
  product: object;
};

const parseActivationJWS = (rawJson: string): { features: VersionedFeature[]; deviceInfo: DeviceInfo | null } => {
  try {
    const outer = JSON.parse(rawJson);
    const activationJWS: string | undefined = get(outer, 'jstar.activationJWS');
    if (!activationJWS) return { features: [], deviceInfo: null };
    const payloadB64 = activationJWS.split('.')[1];
    const decoded = atob(payloadB64.replace(/-/g, '+').replace(/_/g, '/'));
    const parsed = JSON.parse(decoded);
    const features = (parsed.versionedFeatures || []).filter((f: any) => !!f.data);
    const deviceInfo: DeviceInfo | null =
      parsed.product?.code && parsed.hostInfo?.model?.id && parsed.hostInfo?.manufacturer?.id
        ? {
            productCode: parsed.product.code,
            modelId: parsed.hostInfo.model.id,
            manufacturerId: parsed.hostInfo.manufacturer.id,
            licenseId: outer.licenseId ?? '',
            serialNumber: outer.serialNumber ?? '',
            device: outer.device ?? {},
            product: outer.product ?? {},
          }
        : null;
    return { features, deviceInfo };
  } catch {
    return { features: [], deviceInfo: null };
  }
};

const SEARCH_BY_OPTIONS = [
  { value: 'email', label: 'Customer email' },
  { value: 'order', label: 'Order reference (eg: FastSpring)' },
];

const buildResults = (inboundList: any[], outboundList: any[]) => {
  const inboundByPool: Record<string, any> = {};
  for (const ib of inboundList) {
    const poolUuid = ib.pool?.uuid;
    if (!poolUuid) continue;
    if (!inboundByPool[poolUuid] || ib.created?.at > inboundByPool[poolUuid].created?.at) {
      inboundByPool[poolUuid] = ib;
    }
  }
  const deduped = uniqBy(
    orderBy(outboundList, ['created.at'], ['desc']),
    (o: any) => o.destination?.entitlement
  ).filter((o: any) => !!o.destination?.entitlement);
  return deduped.map((o: any) => {
    const inbound = inboundByPool[o.pool?.uuid];
    return {
      entitlementId: o.destination.entitlement,
      source: inbound?.source ?? null,
      date: inbound?.created?.at ?? o.created?.at ?? null,
    };
  });
};

const RefundEligibility: React.FC<RouteProps> = () => {
  const { message, modal } = App.useApp();
  const { instance } = useMsal();
  const themeContext = useContext(ThemeContext as React.Context<any>);

  const [searchInput, setSearchInput] = useState('');
  const [searchType, setSearchType] = useState<'email' | 'order'>('email');
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState<any[]>([]);
  const [searched, setSearched] = useState(false);
  const [featuresLoading, setFeaturesLoading] = useState(false);
  const [selectedEntitlement, setSelectedEntitlement] = useState<string | null>(null);
  const [versionedFeatures, setVersionedFeatures] = useState<VersionedFeature[]>([]);
  const [deviceInfo, setDeviceInfo] = useState<DeviceInfo | null>(null);
  const [selectedSource, setSelectedSource] = useState<any>(null);
  const [resetting, setResetting] = useState(false);
  const [isAlready0x0, setIsAlready0x0] = useState(false);

  const handleViewFeatures = useCallback(async (entitlementId: string, source: any) => {
    setSelectedEntitlement(entitlementId);
    setSelectedSource(source);
    setVersionedFeatures([]);
    setDeviceInfo(null);
    setIsAlready0x0(false);
    setFeaturesLoading(true);

    try {
      const client = getClient(ClientType.poolAllocations, instance);
      const res = await client.query({
        query: gql`${EntitlementActivationJSONQuery}`,
        variables: { entitlementId },
      });
      const raw: string | undefined = get(res, 'data.getEntitlementActivationJSON');
      if (!raw) {
        message.error('No activation data found for this entitlement.');
        return;
      }
      const { features, deviceInfo: info } = parseActivationJWS(raw);
      if (!features.length) {
        message.info('No versioned feature data found in activation JWS.');
      }
      setVersionedFeatures([...features].sort((a, b) => parseFloat(b.version) - parseFloat(a.version)));
      const limitsFeature = features.find(f => f.key === 'LIMITS' && f.version === '1.0');
      setIsAlready0x0(
        !!limitsFeature &&
        Number(limitsFeature.data?.value?.rx) === 0 &&
        Number(limitsFeature.data?.value?.tx) === 0
      );
      setDeviceInfo(info);
    } catch (e) {
      console.error(e);
      message.error('Failed to retrieve entitlement features.');
    } finally {
      setFeaturesLoading(false);
    }
  }, [instance]);

  const handleSearch = useCallback(async () => {
    const trimmed = searchInput.trim();
    if (!trimmed) {
      message.warning(`Please enter a ${searchType === 'email' ? 'customer email' : 'order reference'}.`);
      return;
    }
    setSearching(true);
    setResults([]);
    setSearched(false);
    setSelectedEntitlement(null);
    setSelectedSource(null);
    setVersionedFeatures([]);
    setDeviceInfo(null);
    setIsAlready0x0(false);

    try {
      const client = getClient(ClientType.poolAllocations, instance);
      let merged: any[] = [];

      if (searchType === 'email') {
        const poolsRes = await client.query({
          query: gql`
            query getPoolUUIDsByEmail($q: String) {
              pools(query: { owner: { email: $q }, name: "IFE User-Pays Order (fastspring)" }) {
                uuid
              }
            }
          `,
          variables: { q: trimmed },
        });
        const uuids: string[] = (poolsRes.data?.pools || []).map((p: any) => p.uuid);

        if (!uuids.length) {
          setSearched(true);
          setSearching(false);
          return;
        }

        const [inboundRes, outboundRes] = await Promise.all([
          client.query({
            query: gql`
              query getInboundByPools($uuids: [String!]) {
                inboundAllocations(query: { pool: { uuid_in: $uuids } }) {
                  source {
                    name
                    reference
                    externalId
                  }
                  created {
                    at
                  }
                  pool {
                    uuid
                  }
                }
              }
            `,
            variables: { uuids },
          }),
          client.query({
            query: gql`
              query getOutboundByPools($uuids: [String!]) {
                outboundAllocations(query: { pool: { uuid_in: $uuids } }) {
                  created {
                    at
                  }
                  destination {
                    entitlement
                  }
                  pool {
                    uuid
                  }
                }
              }
            `,
            variables: { uuids },
          }),
        ]);
        merged = buildResults(inboundRes.data?.inboundAllocations || [], outboundRes.data?.outboundAllocations || []);
      } else {
        // Order search: get inbound by order reference, then outbound by pool UUIDs
        const inboundRes = await client.query({
          query: gql`
            query getInboundByOrderRef($q: String) {
              inboundAllocations(query: { source: { reference: $q } }) {
                source {
                  name
                  reference
                  externalId
                }
                created {
                  at
                }
                pool {
                  uuid
                }
              }
            }
          `,
          variables: { q: trimmed },
        });
        const inboundList: any[] = inboundRes.data?.inboundAllocations || [];
        const uuids = Array.from(new Set(inboundList.map((ib: any) => ib.pool?.uuid).filter(Boolean))) as string[];

        if (!uuids.length) {
          setSearched(true);
          setSearching(false);
          return;
        }

        const outboundRes = await client.query({
          query: gql`
            query getOutboundByPools($uuids: [String!]) {
              outboundAllocations(query: { pool: { uuid_in: $uuids } }) {
                created {
                  at
                }
                destination {
                  entitlement
                }
                pool {
                  uuid
                }
              }
            }
          `,
          variables: { uuids },
        });
        merged = buildResults(inboundList, outboundRes.data?.outboundAllocations || []);
      }

      setResults(merged);

      // Auto-load features when there's only one result
      if (merged.length === 1) {
        handleViewFeatures(merged[0].entitlementId, merged[0].source);
      }
    } catch (e) {
      console.error(e);
      message.error('Search failed. Please try again.');
    } finally {
      setSearching(false);
      setSearched(true);
    }
  }, [searchInput, searchType, instance, handleViewFeatures]);

  const handleReset = useCallback(() => {
    if (!selectedEntitlement || !deviceInfo) return;
    const account = instance.getActiveAccount();
    if (!hasOrderAccess(account) && !hasWriteAccess(account)) {
      message.error('You do not have permission to reset device features.');
      return;
    }
    const loggedInEmail = account?.username ?? '';

    modal.confirm({
      title: 'Reset Features',
      content: `This will reset device features for entitlement ${selectedEntitlement} back to 0x0. This cannot be undone. Are you sure?`,
      okText: 'Reset',
      okButtonProps: { danger: true },
      cancelText: 'Cancel',
      onOk: async () => {
        setResetting(true);
        try {
          const configClient = getClient(ClientType.productConfiguration, instance);
          const configRes = await configClient.query({
            query: gql`
              query getBaseSKU($manufacturerId: String!, $modelId: String!) {
                configuration(query: { manufacturerId: $manufacturerId, modelId: $modelId }) {
                  baseSKU { sku }
                }
              }
            `,
            variables: { manufacturerId: deviceInfo.manufacturerId, modelId: deviceInfo.modelId },
          });
          const baseSKU: string | undefined = get(configRes, 'data.configuration.baseSKU.sku');
          if (!baseSKU) {
            message.error('No product configuration found for this device. Reset blocked.');
            return;
          }
          const poolClient = getClient(ClientType.poolAllocations, instance);
          const resetRes = await poolClient.mutate({
            mutation: gql`${ResetDeviceFeatures}`,
            variables: {
              input: {
                name: 'IFE User-Pays Refund (fastspring)',
                productSKU: baseSKU,
                initiatedBy: loggedInEmail,
                deviceInfo,
              },
            },
          });
          const resetError: string | undefined = get(resetRes, 'data.resetDeviceFeatures.error');
          if (resetError) {
            message.error(`Reset failed: ${resetError}`);
            return;
          }

          message.success(`Features reset successfully for ${selectedEntitlement}. Refreshing features...`);
          await handleViewFeatures(selectedEntitlement, selectedSource);
        } catch (e) {
          console.error(e);
          message.error('Reset failed. Please try again.');
        } finally {
          setResetting(false);
        }
      },
    });
  }, [selectedEntitlement, selectedSource, deviceInfo, instance, handleViewFeatures]);

  const account = instance.getActiveAccount();
  if (!hasOrderAccess(account) && !hasWriteAccess(account)) {
    return <AuthError error={undefined} message="You do not have access to this page." />;
  }

  const columns = [
    {
      accessor: 'source',
      Header: 'Order Reference',
      Cell: ({ cell }: { cell: any }) => formatFastspringLink(cell.value) ?? '-',
    },
    {
      accessor: 'date',
      Header: 'Date / Time',
      Cell: ({ cell }: { cell: any }) => cell.value ? formatTimestamp(cell.value) : '-',
    },
    {
      accessor: 'entitlementId',
      Header: 'Action',
      Cell: ({ cell }: { cell: any }) =>
        cell.value ? (
          <Button size="small" onClick={() => handleViewFeatures(cell.value, cell.row.original.source)}>
            View device features
          </Button>
        ) : null,
    },
  ];

  const tabItems = versionedFeatures.map((f, i) => ({
    key: String(i),
    label: <Text code>{f.key} @ {f.version}</Text>,
    children: (
      <pre style={{ margin: 0, fontSize: 12, background: '#f5f5f5', padding: 12, borderRadius: 4, overflowX: 'auto' }}>
        {JSON.stringify(f.data, null, 2)}
      </pre>
    ),
  }));

  return (
    <DataLayout title="Refund Validation">
      <Alert
        type="warning"
        showIcon
        message="Reactivation or recovery of devices where features are lost due to fingerprint changes is not part of the refund eligibility. However, we can reset features associated with the previous fingerprint used for the initial activation purchase."
      />
      {/* Two-column layout: instructions left, features right */}
      <div style={{ marginTop: 24, display: 'flex', flexWrap: 'wrap', gap: 24, alignItems: 'flex-start' }}>

        {/* Left: search bar stacked above instructions card — width matches search controls */}
        <div style={{ flex: '0 0 auto' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
            <Text>Search by:</Text>
            <div style={{ position: 'relative', zIndex: 2, width: 230 }}>
              <Select
                styles={{ control: base => ({ ...base, height: 40, minHeight: 40 }) }}
                theme={theme => ({
                  ...theme,
                  colors: {
                    ...theme.colors,
                    primary25: themeContext.colour.darkSilver,
                    primary: themeContext.colour.mediumGrey,
                  },
                })}
                isSearchable={false}
                value={SEARCH_BY_OPTIONS.find(o => o.value === searchType)}
                options={SEARCH_BY_OPTIONS}
                onChange={(val: SingleValue<{ value: string; label: string }>) => {
                  if (!val) return;
                  setSearchType(val.value as 'email' | 'order');
                  setSearchInput('');
                  setSearched(false); setResults([]); setSelectedEntitlement(null); setSelectedSource(null); setVersionedFeatures([]); setDeviceInfo(null); setIsAlready0x0(false);
                }}
              />
            </div>
            <Input
              placeholder={searchType === 'email' ? 'Customer email' : 'Order reference'}
              style={{ width: 300 }}
              value={searchInput}
              onChange={e => {
                const val = e.target.value;
                setSearchInput(val);
                // Clear results only when the input is fully emptied
                if (!val) { setSearched(false); setResults([]); setSelectedEntitlement(null); setSelectedSource(null); setVersionedFeatures([]); setDeviceInfo(null); setIsAlready0x0(false); }
              }}
              onPressEnter={handleSearch}
            />
            <Button type="primary" icon={<SearchOutlined />} loading={searching} onClick={handleSearch}>
              Search
            </Button>
          </div>
          <div style={{ background: '#fff', border: '1px solid #d9d9d9', borderRadius: 8, padding: '20px 24px' }}>
            <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 12 }}>Instructions</div>
            <ol style={{ margin: 0, paddingLeft: 18, fontSize: 14, lineHeight: 1.8, color: '#262626' }}>
              <li>Search by customer email or order reference to see their order history.</li>
              <li>If multiple results appear, click <strong>View Device Features</strong> on a row. For a single result, features load automatically.</li>
              <li>Click <strong>Reset Features</strong> to zero out the device features (0x0).</li>
              <li>Once reset, proceed to refund the order in FastSpring.</li>
              <li>Use the Entitlement ID link to verify the reset was applied.</li>
            </ol>
          </div>
        </div>

        {/* Right: Features panel */}
        <div style={{ flex: 1, minWidth: 0 }}>
          {selectedEntitlement !== null ? (
              <div style={{ background: '#fff', border: '1px solid #d9d9d9', borderRadius: 8, overflow: 'hidden' }}>

                {/* Header */}
                <div style={{ padding: '20px 28px', borderBottom: '1px solid #f0f0f0' }}>
                  <div style={{ fontSize: 13, color: '#434343', marginBottom: 4 }}>Current Features for Order</div>
                  <div style={{ fontSize: 20, fontWeight: 600, lineHeight: 1.3 }}>
                    {formatFastspringLink(selectedSource) ?? <span style={{ fontFamily: 'monospace' }}>{selectedEntitlement}</span>}
                  </div>
                  <div style={{ marginTop: 6, fontSize: 14, color: '#262626' }}>
                    Entitlement ID:&nbsp;
                    <a
                      href={`${PATHS.ENTITLEMENTS}/?search=${selectedEntitlement}&category=entitlementId`}
                      target="_blank"
                      rel="noreferrer"
                      style={{ fontFamily: 'monospace', fontSize: 13, color: '#0000EE', textDecoration: 'underline' }}
                    >
                      {selectedEntitlement}
                    </a>
                  </div>
                </div>

                {/* Device metadata */}
                {deviceInfo && (
                  <div style={{ padding: '16px 28px', borderBottom: '1px solid #f0f0f0', display: 'flex', gap: 48, flexWrap: 'wrap', background: '#fafafa' }}>
                    <div>
                      <div style={{ fontSize: 12, color: '#434343', marginBottom: 3 }}>Product Code</div>
                      <div style={{ fontSize: 15, fontWeight: 600 }}>{deviceInfo.productCode}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: 12, color: '#434343', marginBottom: 3 }}>Model ID</div>
                      <div style={{ fontFamily: 'monospace', fontSize: 14 }}>{deviceInfo.modelId}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: 12, color: '#434343', marginBottom: 3 }}>Manufacturer ID</div>
                      <div style={{ fontFamily: 'monospace', fontSize: 14 }}>{deviceInfo.manufacturerId}</div>
                    </div>
                  </div>
                )}

                {/* Features + actions */}
                <div style={{ padding: '20px 28px' }}>
                  {featuresLoading
                    ? <Loader />
                    : tabItems.length > 0
                      ? (
                          <>
                            <Tabs items={tabItems} />
                            <div style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 12 }}>
                              {isAlready0x0 && (
                                <Text type="secondary" style={{ fontSize: 13 }}>Device is already at 0x0 (base feature active) — nothing to reset.</Text>
                              )}
                              <Button
                                type="primary"
                                loading={resetting}
                                disabled={resetting || isAlready0x0}
                                style={isAlready0x0 ? { pointerEvents: 'none' } : undefined}
                                onClick={handleReset}
                              >
                                Reset Features
                              </Button>
                            </div>
                          </>
                        )
                      : null
                  }
                </div>

              </div>
            ) : (
              <div style={{ background: '#fff', border: '1px dashed #d9d9d9', borderRadius: 8, padding: '40px 28px', textAlign: 'center', color: '#595959', fontSize: 14 }}>
                {!searched
                  ? `Search for a ${searchType === 'email' ? 'customer email' : 'order reference'} to get started`
                  : results.length === 0
                    ? `No results found for "${searchInput}"`
                    : 'Select an order from the table to view device features'}
              </div>
            )}
        </div>

      </div>

      {/* Results table — only shown when there are multiple results */}
      <>
        {searched && !searching && results.length > 1
          ? <div style={{ marginTop: 24 }}><Table data={results} columns={columns} sorting={false} /></div>
          : null
        }
      </>
    </DataLayout>
  );
};

export default RefundEligibility;
